import type { WorkspaceAppearance, WorkspaceCabinet, WorkspacePayload } from "@wms/domain";
import type { ThemeConfig } from "antd";
import { create } from "zustand";
import {
  DEFAULT_APPEARANCE,
  type LookTokens,
  type Palette,
  type ResolvedLook,
  lookProblems,
  lookTokens,
  mergePalette,
  resolveAppearance,
  rgba,
} from "./palette";

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-10…12, UXW-15, UXW-17, CW-05…09): which personal look the page shows. The cabinet
 * is decided by the address, the look belongs to the signed-in principal of that cabinet only, and nothing personal is
 * shown in the dark theme, with the option off or to a signed-out visitor — then the screen is exactly as before.
 */

/** The cabinet of an address; null — a page outside both cabinets (sign-in pages, the public signing link). */
export function cabinetOfPath(pathname: string): WorkspaceCabinet | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/login" || path === "/portal/login" || path.startsWith("/sign/")) return null;
  if (path === "/portal" || path.startsWith("/portal/")) return "portal";
  return "staff";
}

export interface PrincipalLook {
  id: string | null;
  signedIn: boolean;
  /** `use_personal_workspace` as this principal's profile reports it. */
  option: boolean;
  /** The cabinet is not open yet (a temporary password): only the standard look, never a stored one (CW-04). */
  locked?: boolean;
}

export interface LookPreview {
  cabinet: WorkspaceCabinet;
  principal: string;
  appearance: WorkspaceAppearance;
}

export interface LookInput {
  pathname: string;
  theme: "light" | "dark";
  staff: PrincipalLook;
  portal: PrincipalLook;
  /** The saved look of the principal of the active cabinet: the server answer, else its own scoped cache. */
  saved?: WorkspaceAppearance | null;
  palette?: Palette | null;
  /** The choice on the «Оформление» screen before «Сохранить» (UXW-16). */
  preview?: LookPreview | null;
}

export interface ActiveLook extends ResolvedLook {
  cabinet: WorkspaceCabinet;
  principal: string;
  tokens: LookTokens;
  /** The look on the screen is the unsaved choice of the settings. */
  previewing: boolean;
}

export function decideLook(input: LookInput): ActiveLook | null {
  const cabinet = cabinetOfPath(input.pathname);
  if (!cabinet) return null;
  // CW-07: the dark theme keeps its own background and accent
  if (input.theme !== "light") return null;
  const who = cabinet === "portal" ? input.portal : input.staff;
  // CW-09: the look of the cabinet on the screen only; a session of the other cabinet in this browser does not count
  if (!who.signedIn || !who.id || !who.option) return null;
  const palette = mergePalette(input.palette);
  const standard = DEFAULT_APPEARANCE[cabinet];
  let appearance = who.locked ? standard : input.saved ?? standard;
  let previewing = false;
  const preview = input.preview;
  if (!who.locked && preview && preview.cabinet === cabinet && preview.principal === who.id) {
    // UXW-14: an unsupported combination is not put on the page; the screen keeps the last good look
    if (lookProblems(resolveAppearance(preview.appearance, cabinet, palette)).length === 0) {
      appearance = preview.appearance;
      previewing = true;
    }
  }
  let resolved = resolveAppearance(appearance, cabinet, palette);
  if (lookProblems(resolved).length) resolved = resolveAppearance(standard, cabinet, palette);
  return { ...resolved, cabinet, principal: who.id, tokens: lookTokens(resolved), previewing };
}

/** A short identity of a look: the store is updated only when it changes. */
export function lookSignature(look: ActiveLook | null): string {
  return look ? [look.cabinet, look.principal, look.background, look.accent, look.canvas ?? "-", look.accentHex].join("|") : "none";
}

// UXW-17 / CW-09: the only local copy is per cabinet and per principal — another person in this browser never starts
// from it; it holds what the server confirmed, never an unsaved preview
export function lookCacheKey(cabinet: WorkspaceCabinet, principal: string): string {
  return `wms-look:${cabinet}:${principal}`;
}

export interface CachedLook {
  appearance: WorkspaceAppearance;
  palette: Palette;
}

export function readCachedLook(cabinet: WorkspaceCabinet, principal: string | null | undefined): CachedLook | null {
  if (!principal) return null;
  try {
    const raw = localStorage.getItem(lookCacheKey(cabinet, principal));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<CachedLook>;
    const appearance = value.appearance;
    if (!appearance || typeof appearance.background !== "string" || typeof appearance.accent !== "string") return null;
    return { appearance: { background: appearance.background, accent: appearance.accent }, palette: mergePalette(value.palette) };
  } catch {
    return null;
  }
}

export function writeCachedLook(cabinet: WorkspaceCabinet, principal: string, payload: WorkspacePayload): void {
  const appearance = payload.workspace.appearance;
  // only the two chosen entries of the palette: enough to paint the next start before the server answers
  const palette: Palette = {
    backgrounds: payload.palette.backgrounds.filter((b) => b.id === appearance.background),
    accents: payload.palette.accents.filter((a) => a.id === appearance.accent),
  };
  try {
    localStorage.setItem(lookCacheKey(cabinet, principal), JSON.stringify({ appearance, palette }));
  } catch {
    /* private mode or a full storage: the look comes from the server next time */
  }
}

/**
 * The antd theme with the look: the accent becomes primary, link and selected; the canvas — the background of the
 * layout. Error, warning, success and info tokens are untouched (UXW-15), so statuses, danger buttons and
 * «processing» tags keep their colours. Without a look the base theme is returned as is.
 */
export function themeWithLook(base: ThemeConfig, look: ActiveLook | null): ThemeConfig {
  if (!look) return base;
  const t = look.tokens;
  const components = base.components ?? {};
  return {
    ...base,
    token: {
      ...base.token,
      colorPrimary: t.primary,
      colorPrimaryHover: t.primaryHover,
      colorPrimaryActive: t.primaryActive,
      colorLink: t.link,
      colorLinkHover: t.linkHover,
      colorLinkActive: t.linkActive,
    },
    components: {
      ...components,
      Layout: { ...components.Layout, ...(t.canvas ? { bodyBg: t.canvas } : {}) },
      Menu: { ...components.Menu, itemSelectedBg: t.soft, itemSelectedColor: t.softInk },
      Button: { ...components.Button, primaryShadow: `0 1px 2px ${rgba(t.primary, 0.25)}` },
      Input: { ...components.Input, activeShadow: `0 0 0 3px ${rgba(t.primary, 0.15)}` },
    },
  };
}

const LOOK_VARIABLES = ["--wms-accent", "--wms-accent-soft", "--wms-page-bg", "--wms-muted"] as const;

/** The CSS side of the look on `<html>`: accent, its soft tint, the canvas and a readable secondary text. */
export function applyLookVariables(root: HTMLElement, look: ActiveLook | null): void {
  for (const name of LOOK_VARIABLES) root.style.removeProperty(name);
  if (!look) {
    delete root.dataset.look;
    return;
  }
  root.dataset.look = look.cabinet;
  root.style.setProperty("--wms-accent", look.tokens.focus);
  root.style.setProperty("--wms-accent-soft", look.tokens.soft);
  if (look.tokens.canvas) root.style.setProperty("--wms-page-bg", look.tokens.canvas);
  if (look.tokens.muted) root.style.setProperty("--wms-muted", look.tokens.muted);
}

interface LookState {
  active: ActiveLook | null;
  preview: LookPreview | null;
  setActive: (look: ActiveLook | null) => void;
  setPreview: (preview: LookPreview | null) => void;
}

/** The look on the screen (read by the root theme) and the unsaved choice of the settings screen. */
export const useLookStore = create<LookState>()((set, get) => ({
  active: null,
  preview: null,
  setActive(look) {
    if (lookSignature(look) !== lookSignature(get().active) || look?.previewing !== get().active?.previewing) set({ active: look });
  },
  setPreview(preview) {
    set({ preview });
  },
}));
